import {
  StudentOutstandingDetail,
  ClassWiseTotal,
  GrandTotal,
  ClassGroup
} from '../types';

export const SCHOOLS_EXCLUDING_BOUNCE_CHARGES = ['BPIS'];

export const classKey = (row: { grade: string; division: string }) =>
  `${row.grade}|||${row.division}`;

export const extractParticularKeys = (
  studentDetails: StudentOutstandingDetail[]
): string[] =>
  Array.from(
    new Set(
      studentDetails.flatMap((student) => Object.keys(student.particulars))
    )
  ).sort();

export const buildClassGroups = (
  studentDetails: StudentOutstandingDetail[],
  classWiseTotal: ClassWiseTotal[]
): ClassGroup[] => {
  const classGroupMap = new Map<string, StudentOutstandingDetail[]>();
  studentDetails.forEach((student) => {
    const key = classKey(student);
    if (!classGroupMap.has(key)) classGroupMap.set(key, []);
    classGroupMap.get(key)!.push(student);
  });
  return classWiseTotal.map((classTotal) => ({
    classTotal,
    students: classGroupMap.get(classKey(classTotal)) ?? []
  }));
};

const accumulateParticulars = (
  target: Record<string, number>,
  source: Record<string, number>
) => {
  Object.entries(source).forEach(([key, val]) => {
    target[key] = (target[key] ?? 0) + val;
  });
};

export const mergeStudentRows = (
  studentDetails: StudentOutstandingDetail[]
): StudentOutstandingDetail[] => {
  const mergedStudentsMap = new Map<string, StudentOutstandingDetail>();

  studentDetails.forEach((student) => {
    const key = `${classKey(student)}|||${student.studentId}`;
    const existingStudent = mergedStudentsMap.get(key);
    if (!existingStudent) {
      mergedStudentsMap.set(key, {
        ...student,
        particulars: { ...student.particulars }
      });
    } else {
      accumulateParticulars(existingStudent.particulars, student.particulars);
      existingStudent.lateFee = existingStudent.lateFee + student.lateFee;
      existingStudent.chequeBounceFee =
        existingStudent.chequeBounceFee + student.chequeBounceFee;
      existingStudent.total = existingStudent.total + student.total;
    }
  });

  return Array.from(mergedStudentsMap.values());
};

interface FeeBreakdown {
  particulars: Record<string, number>;
  particularKeys: string[];
  lateFee: number;
  chequeBounceFee: number;
  total: number;
}

const applyFeeFields = (
  row: Record<string, unknown>,
  fee: FeeBreakdown,
  includeBounceCharges = true
) => {
  fee.particularKeys.forEach((k) => {
    row[k] = fee.particulars[k] ?? 0;
  });
  row['Late Fee'] = fee.lateFee;
  if (includeBounceCharges) {
    row['Bounce Charges'] = fee.chequeBounceFee;
  }
  row['Total'] = fee.total;
};

export const mergeClassWiseTotals = (
  classWiseTotalPerPage: ClassWiseTotal[][]
): ClassWiseTotal[] => {
  const classWiseTotalMap = new Map<string, ClassWiseTotal>();

  classWiseTotalPerPage.forEach((page) => {
    page.forEach((pageTotalEntry) => {
      const classGroupKey = classKey(pageTotalEntry);
      const existingClassTotal = classWiseTotalMap.get(classGroupKey);
      if (!existingClassTotal) {
        classWiseTotalMap.set(classGroupKey, {
          ...pageTotalEntry,
          particularTotals: { ...pageTotalEntry.particularTotals }
        });
      } else {
        accumulateParticulars(
          existingClassTotal.particularTotals,
          pageTotalEntry.particularTotals
        );
        existingClassTotal.lateFeeTotal =
          existingClassTotal.lateFeeTotal + pageTotalEntry.lateFeeTotal;
        existingClassTotal.chequeBounceFeeTotal =
          existingClassTotal.chequeBounceFeeTotal +
          pageTotalEntry.chequeBounceFeeTotal;
        existingClassTotal.total =
          existingClassTotal.total + pageTotalEntry.total;
      }
    });
  });

  const result = Array.from(classWiseTotalMap.values()).sort(
    (a, b) =>
      a.grade.localeCompare(b.grade, undefined, { numeric: true }) ||
      a.division.localeCompare(b.division, undefined, { numeric: true })
  );

  return result;
};

export const mergeGrandTotals = (totals: GrandTotal[]): GrandTotal => {
  const particularTotals: Record<string, number> = {};

  totals.forEach((pageGrandTotal) =>
    accumulateParticulars(particularTotals, pageGrandTotal.particularTotals)
  );

  return {
    particularTotals,
    lateFeeTotal: totals.reduce(
      (sum, pageGrandTotal) => sum + pageGrandTotal.lateFeeTotal,
      0
    ),
    chequeBounceFeeTotal: totals.reduce(
      (sum, pageGrandTotal) => sum + pageGrandTotal.chequeBounceFeeTotal,
      0
    ),
    total: totals.reduce((sum, pageGrandTotal) => sum + pageGrandTotal.total, 0)
  };
};

export const buildDownloadRows = (
  studentDetails: StudentOutstandingDetail[],
  classWiseTotal: ClassWiseTotal[],
  grandTotal: GrandTotal,
  school: string
): Record<string, unknown>[] => {
  const particularKeys = extractParticularKeys(studentDetails);
  const classGroups = buildClassGroups(studentDetails, classWiseTotal);
  const downloadRows: Record<string, unknown>[] = [];
  const includeBounceCharges =
    !SCHOOLS_EXCLUDING_BOUNCE_CHARGES.includes(school);

  classGroups.forEach(({ classTotal, students }) => {
    downloadRows.push({
      '': classTotal.division
        ? `Class ${classTotal.grade} — ${classTotal.division}`
        : `Class ${classTotal.grade} (Students with unassigned divisions)`,
      'Roll No': '',
      'Student Name': ''
    });

    students.forEach((student) => {
      const studentRow: Record<string, unknown> = {
        '': '',
        'Roll No': student.rollNo,
        'Student Name': student.name
      };
      applyFeeFields(
        studentRow,
        {
          particulars: student.particulars,
          particularKeys,
          lateFee: student.lateFee,
          chequeBounceFee: student.chequeBounceFee,
          total: student.total
        },
        includeBounceCharges
      );
      downloadRows.push(studentRow);
    });

    const subtotalRow: Record<string, unknown> = {
      '': 'Total',
      'Roll No': '',
      'Student Name': ''
    };
    applyFeeFields(
      subtotalRow,
      {
        particulars: classTotal.particularTotals,
        particularKeys,
        lateFee: classTotal.lateFeeTotal,
        chequeBounceFee: classTotal.chequeBounceFeeTotal,
        total: classTotal.total
      },
      includeBounceCharges
    );
    downloadRows.push(subtotalRow);
    downloadRows.push({ '': '', 'Roll No': '', 'Student Name': '' });
  });

  const grandTotalRow: Record<string, unknown> = {
    '': 'Grand Total',
    'Roll No': '',
    'Student Name': ''
  };
  applyFeeFields(
    grandTotalRow,
    {
      particulars: grandTotal.particularTotals,
      particularKeys,
      lateFee: grandTotal.lateFeeTotal,
      chequeBounceFee: grandTotal.chequeBounceFeeTotal,
      total: grandTotal.total
    },
    includeBounceCharges
  );
  downloadRows.push(grandTotalRow);

  return downloadRows;
};

export const formatNumber = (value: number) => value.toLocaleString('en-IN');
